import React from "react"
import { graphql } from "gatsby"
import { StructuredText } from "react-datocms"
import {
  isHeading,
  isParagraph,
  renderRule,
} from "datocms-structured-text-utils"
import { Box, Text, Container } from "@theme-ui/components"
import Layout from "../components/layout"
import { getStoryPath } from "../utils/path"
import { GatsbyImage } from "gatsby-plugin-image"
import ImageGallery from "../components/blocks/imageGallery"
import ArticleTitle from "../components/articleTitle"
import { HelmetDatoCms } from "gatsby-source-datocms"
import Embed from "../components/blocks/embed"
import FormEmbed from "../components/blocks/formEmbed"
import { i18nContext } from "../context/i18nContext"
import FilterMetaTagDescription from "../utils/filterMetaTags"

const Story = ({ data: { page } }) => {
  // console.log(page)
  const i18nPaths = page._allSlugLocales.map(path => {
    return {
      locale: path.locale,
      value: getStoryPath(page, path.locale),
    }
  })

  return (
    <Layout locale={page.locale} i18nPaths={i18nPaths}>
      <HelmetDatoCms seo={FilterMetaTagDescription(page.seoMetaTags)}>
        <html lang={page.locale} />
      </HelmetDatoCms>
      <Box
        sx={{
          position: "relative",
          ".gatsby-image-wrapper": {
            width: "100%",
            display: "block",
            minHeight: "18rem",
            maxHeight: "40rem",
            mt: ["6rem", "6rem", 0, 0],
            overflow: "hidden",
          },
        }}
      >
        <GatsbyImage image={page.heroImage.article} />
        <Box
          sx={{
            position: "absolute",
            height: "100%",
            width: "100%",
            top: 0,
            left: 0,
            backgroundColor: "dark",
            opacity: 0.3,
          }}
        ></Box>
      </Box>
      <ArticleTitle page={page} />
      <Container variant="sm" sx={{ mb: [5, 6, 6] }}>
        <StructuredText
          data={page.body}
          customRules={[
            renderRule(
              isHeading,
              ({ adapter: { renderNode }, node, children, key }) => {
                return renderNode(
                  () => {
                    return (
                      <Text
                        as={`h${node.level}`}
                        variant={`h${node.level}`}
                        sx={{ mb: 3 }}
                      >
                        {children}
                      </Text>
                    )
                  },
                  { key },
                  children
                )
              }
            ),
            renderRule(
              isParagraph,
              ({ adapter: { renderNode }, node, children, key }) => {
                return renderNode(
                  () => {
                    return (
                      <Text as="p" mb={3} variant="article">
                        {children}
                      </Text>
                    )
                  },
                  { key },
                  children
                )
              }
            ),
          ]}
          renderBlock={({ record }) => {
            switch (record.__typename) {
              case "DatoCmsImageGallery":
                return (
                  <Box mt={5} mb={5}>
                    <ImageGallery
                      images={record.images}
                      fullWidth={record.fullWidth}
                      id={record.id}
                      grid={record.grid}
                    />
                  </Box>
                )
              case "DatoCmsEmbed":
                return <Embed code={record.code} fullWidth={record.fullWidth} />
              default:
                return null
            }
          }}
        />
      </Container>
      {page.form && (
        <i18nContext.Consumer>
          {t => (
            <FormEmbed
              title={t.caseStudy}
              dataFormBlockId={page.form.dataFormBlockId}
              dataWebsiteId={page.form.dataWebsiteId}
              dataHostname={page.form.dataHostname}
              divId={page.form.divId}
            />
          )}
        </i18nContext.Consumer>
      )}
    </Layout>
  )
}

export default Story

export const query = graphql`
  query StoryQuery($id: String!, $locale: String!) {
    page: datoCmsStory(id: { eq: $id }) {
      ...StoryDetails
      ...StoryAllSlugLocales
      ...StoryMeta
      meta {
        firstPublishedAt(locale: $locale, formatString: "DD MMMM Y")
      }
      seoMetaTags {
        ...GatsbyDatoCmsSeoMetaTags
      }
      form {
        ...DynamicsFormDetails
      }
    }
  }

  fragment StoryMeta on DatoCmsStory {
    meta {
      firstPublishedAt(locale: $locale, formatString: "DD MMMM Y")
    }
  }

  fragment StoryAllSlugLocales on DatoCmsStory {
    _allSlugLocales {
      value
      locale
    }
  }

  fragment StoryDetails on DatoCmsStory {
    id
    locale
    position
    title
    slug
    company
    model {
      apiKey
    }
    heroImage {
      article: gatsbyImageData(
        width: 1440
        placeholder: BLURRED
        forceBlurhash: false
      )
      list: gatsbyImageData(
        width: 600
        height: 300
        placeholder: BLURRED
        forceBlurhash: false
      )
    }
    category {
      id
      title
      slug
      ...StoryCategoryAllSlugLocales
    }
    ...StoryBody
  }

  fragment StoryBody on DatoCmsStory {
    body {
      value
      blocks {
        __typename
        ... on DatoCmsImageGallery {
          id: originalId
          ...ImageGallery
        }
        ... on DatoCmsEmbed {
          id: originalId
          ...EmbedDetails
        }
      }
    }
  }

  fragment ImageGallery on DatoCmsImageGallery {
    images {
      gatsbyImageData(
        placeholder: BLURRED
        forceBlurhash: false
      )
      alt
      title
    }
    grid
    fullWidth
    model {
      apiKey
    }
  }
`
